import { Edit, ReportGmailerrorred, TaskAlt } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo } from 'react';

import { Pagination, VaccineListItem, VaccinePetsListItem } from 'shared/api';
import { ProgressHelp } from 'shared/assets/svg';

type Props = {
  list: Array<VaccineListItem | VaccinePetsListItem>;
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  isPets?: boolean;
  loadingEditId?: string | null;
  loadingDiseaseId?: string | null;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange: (column: string) => void;
  onEdit: (vaccineId: string) => void;
  onDiseaseClick?: (diseaseId: string) => void;
};

export const VaccineTablePaginated = memo(
  ({
    list,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    isPets = false,
    loadingEditId = null,
    loadingDiseaseId = null,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
    onDiseaseClick,
  }: Props) => {
    const columnsCount = isPets ? 8 : 7;
    const handleChangePage = (event: unknown, newPage: number) => {
      onPageChange(newPage + 1); // MUI использует 0-based, API использует 1-based
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      onSortChange(columnId);
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Индикатор загрузки */}
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        {/* Затемнение таблицы при загрузке */}
        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <ProgressHelp />
                  </TableCell>
                  <TableCell width={600}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('name')}
                    >
                      Trade name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'manufacturer'}
                      direction={sortBy === 'manufacturer' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('manufacturer')}
                    >
                      Manufacturing
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'manufacturerCountry'}
                      direction={sortBy === 'manufacturerCountry' ? sortOrder : 'asc'}
                      onClick={() => handleSortRequest('manufacturerCountry')}
                    >
                      Country
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Countries certified</TableCell>
                  <TableCell width={460}>Used in Diseases</TableCell>
                  {isPets && (
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'petType'}
                        direction={sortBy === 'petType' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('petType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell align="right">Controls</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length > 0 ? (
                  list.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell align="center">
                        {row.verified ? <TaskAlt color="success" /> : <ReportGmailerrorred color="warning" />}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <Box>{row.name}</Box>
                      </TableCell>
                      <TableCell>
                        <Box>{row.manufacturer}</Box>
                      </TableCell>
                      <TableCell>
                        <Box>{row.manufacturerCountry}</Box>
                      </TableCell>
                      <TableCell>{row.availableCountriesCount}</TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Box display="flex" flexWrap="wrap">
                          {row.usedInDiseases.length === 0 ? (
                            <Box
                              component="span"
                              p={0.5}
                              m={0.25}
                              sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                borderRadius: '4px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              Not used
                            </Box>
                          ) : (
                            row.usedInDiseases.map((disease) => {
                              const isDiseaseLoading = loadingDiseaseId === disease.id;
                              return (
                                <Box
                                  key={disease.id}
                                  component="span"
                                  p={0.5}
                                  m={0.25}
                                  onClick={() => {
                                    if (!isDiseaseLoading) {
                                      onDiseaseClick?.(disease.id);
                                    }
                                  }}
                                  sx={{
                                    position: 'relative',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '4px',
                                    whiteSpace: 'nowrap',
                                    color: disease.verified ? '#29b194' : 'inherit',
                                    cursor: isDiseaseLoading ? 'default' : onDiseaseClick ? 'pointer' : 'default',
                                    '&:hover':
                                      onDiseaseClick && !isDiseaseLoading
                                        ? {
                                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                          }
                                        : {},
                                  }}
                                >
                                  {isDiseaseLoading && (
                                    <CircularProgress
                                      size={14}
                                      sx={{
                                        color: '#29b194',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-7px',
                                        marginLeft: '-7px',
                                      }}
                                    />
                                  )}
                                  <Box
                                    component="span"
                                    sx={{ opacity: isDiseaseLoading ? 0.3 : 1, transition: 'opacity 0.2s' }}
                                  >
                                    {'name_en' in disease ? disease.name_en : disease.name}
                                  </Box>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </TableCell>
                      {isPets && 'petType' in row && (
                        <TableCell>{row.petType.charAt(0).toUpperCase() + row.petType.slice(1)}</TableCell>
                      )}
                      <TableCell align="right">
                        <IconButton
                          onClick={() => {
                            if (loadingEditId !== row.id) {
                              onEdit(row.id);
                            }
                          }}
                          size="small"
                          disabled={loadingEditId === row.id}
                        >
                          {loadingEditId === row.id ? (
                            <CircularProgress size={20} sx={{ color: '#29b194' }} />
                          ) : (
                            <Edit sx={{ color: '#29b194' }} />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columnsCount} align="center">
                      No vaccines found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1} // MUI использует 0-based
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

VaccineTablePaginated.displayName = 'VaccineTablePaginated';
